import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { api, CREDITS_CHANGED_EVENT } from '../context/AuthContext';
import useCredits from '../hooks/useCredits';

const SOURCE_LABELS = {
  monthly: 'Monthly',
  rollover: 'Rolled over',
  trial: 'Free trial',
  pack: 'Packs',
  promo: 'Bonus',
  refund: 'Refunds',
};

const formatDate = (iso, withTime = false) => (iso ? new Date(iso).toLocaleString(undefined, withTime
  ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
  : { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');  // period dates are UTC midnights

const describe = (entry, prices) => {
  switch (entry.type) {
    case 'commit':
      return prices?.[entry.feature]?.label || entry.feature || 'AI action';
    case 'grant':
      return entry.note || `${SOURCE_LABELS[entry.source] || entry.source} credits`;
    case 'expire':
      return `${SOURCE_LABELS[entry.source] || 'Unused'} credits expired`;
    case 'adjust':
      return entry.note || 'Adjustment';
    default:
      return entry.note || entry.type;
  }
};

/** Billing page: credit balance, monthly allowance, sources and usage history. */
const CreditsCard = () => {
  const { credits, prices } = useCredits();
  const [entries, setEntries] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadHistory = useCallback(async (before = null) => {
    const res = await api.get('/billing/credits/history', { params: { limit: 20, ...(before ? { before } : {}) } });
    setEntries((prev) => (before ? [...prev, ...res.data.entries] : res.data.entries));
    setNextBefore(res.data.next_before);
  }, []);

  useEffect(() => {
    loadHistory().catch(() => {});
    const onChanged = () => loadHistory().catch(() => {});
    window.addEventListener(CREDITS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, onChanged);
  }, [loadHistory]);

  const loadMore = async () => {
    setLoadingMore(true);
    try { await loadHistory(nextBefore); } finally { setLoadingMore(false); }
  };

  if (!credits) {
    return (
      <Card className="border-slate-100">
        <CardContent className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></CardContent>
      </Card>
    );
  }

  const usedPct = credits.allowance ? Math.min(100, Math.round((credits.allowance_used / credits.allowance) * 100)) : 0;
  const sources = Object.entries(credits.by_source || {}).filter(([, n]) => n > 0);

  return (
    <Card className="border-slate-100" data-testid="credits-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-emerald-500" />AI Credits</CardTitle>
        <CardDescription>
          Every AI action uses credits; failed generations are refunded automatically.
          {credits.is_team_pool && ' You are using your team\'s shared credits.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {credits.unlimited ? (
          <p className="text-slate-700">Your account has unlimited AI credits. Usage is still recorded below.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-slate-900" data-testid="credits-balance">
                  {credits.balance.toLocaleString()}
                </p>
                <p className="text-sm text-slate-500">credits available</p>
              </div>
              {credits.renews_at && (
                <p className="text-sm text-slate-500">Renews {formatDate(credits.renews_at)}</p>
              )}
            </div>
            {credits.allowance > 0 && (
              <div>
                <Progress value={usedPct} className="h-2 bg-slate-100 [&>div]:bg-emerald-500" />
                <p className="text-xs text-slate-500 mt-1">
                  {credits.allowance_used.toLocaleString()} of {credits.allowance.toLocaleString()}{' '}
                  {credits.plan_id === 'free_trial' ? 'trial' : 'monthly'} credits used
                </p>
              </div>
            )}
            {sources.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {sources.map(([source, n]) => (
                  <span key={source} className="text-xs rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
                    {SOURCE_LABELS[source] || source}: {n.toLocaleString()}
                  </span>
                ))}
              </div>
            )}
            {credits.plan_id !== 'free_trial' && (
              <p className="text-xs text-slate-500">
                Unused monthly credits roll over for one month. Credit packs never expire.
              </p>
            )}
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold text-slate-900 mb-2">Usage history</h4>
          {entries.length === 0 ? (
            <p className="text-sm text-slate-500">No credit activity yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm" data-testid="credits-history">
              {entries.map((entry) => (
                <li key={entry.entry_id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-slate-700">{describe(entry, prices)}</p>
                    <p className="text-xs text-slate-400">{formatDate(entry.created_at, true)}</p>
                  </div>
                  <span className={entry.amount > 0 ? 'text-emerald-600 font-medium' : 'text-slate-600'}>
                    {entry.amount > 0 ? '+' : ''}{entry.amount.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {nextBefore && (
            <Button variant="ghost" size="sm" className="mt-2" onClick={loadMore} disabled={loadingMore}>
              {loadingMore && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Show more
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CreditsCard;
